import React, { useState, useEffect } from 'react';
import { apiService } from '../api';
import PnFChart from '../components/charts/PnFChart';
import FScoreBreakdown from '../components/charts/FScoreBreakdown';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { CapeScore, FScore, PnFChart as PnFChartType, FinancialData } from '../types';

interface TickerItem {
  ticker: string;
  name: string;
}

const StockAnalysis: React.FC = () => {
  const [tickers, setTickers] = useState<TickerItem[]>([]);
  const [selectedTicker, setSelectedTicker] = useState<string>('');
  const [capeScore, setCapeScore] = useState<CapeScore | null>(null);
  const [fScore, setFScore] = useState<FScore | null>(null);
  const [pnfChart, setPnfChart] = useState<PnFChartType | null>(null);
  const [financialData, setFinancialData] = useState<FinancialData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'financial' | 'technical'>('overview');

  // Fetch tickers on component mount
  useEffect(() => {
    const fetchTickers = async () => {
      try {
        const data = await apiService.getSP500Tickers();
        setTickers(data);
        
        // Log number of tickers fetched
        console.log(`StockAnalysis: Fetched ${data.length} tickers from SP500 list`);
        
        if (data.length > 0) {
          setSelectedTicker(data[0].ticker);
        }
      } catch (error) {
        setError('Failed to fetch tickers');
        console.error('Error fetching tickers:', error);
      }
    };
    
    fetchTickers();
  }, []);

  // Fetch stock analysis when ticker changes
  useEffect(() => {
    const fetchStockAnalysis = async () => {
      if (!selectedTicker) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const data = await apiService.getStockAnalysis(selectedTicker);
        setCapeScore(data.capeScore);
        setFScore(data.fScore);
        setPnfChart(data.pnfChart);
        setFinancialData(data.financialData);
      } catch (error) {
        setError(`Failed to fetch analysis for ${selectedTicker}`);
        console.error(`Error fetching analysis for ${selectedTicker}:`, error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStockAnalysis();
  }, [selectedTicker]);

  // Format F-Score for chart display
  const getFScoreChartData = () => {
    if (!fScore) return [];
    
    return [
      { name: 'ROA > 0', value: fScore.roa ? 1 : 0 },
      { name: 'CFO > 0', value: fScore.cfo ? 1 : 0 },
      { name: 'ROA ↑', value: fScore.deltaRoa ? 1 : 0 },
      { name: 'Accrual', value: fScore.accrual ? 1 : 0 },
      { name: 'Leverage ↓', value: fScore.deltaLeverage ? 1 : 0 },
      { name: 'Liquidity ↑', value: fScore.deltaLiquidity ? 1 : 0 },
      { name: 'No New Shares', value: fScore.noEquityRaise ? 1 : 0 },
      { name: 'Margin ↑', value: fScore.deltaMargin ? 1 : 0 },
      { name: 'Turnover ↑', value: fScore.deltaTurnover ? 1 : 0 },
    ];
  };

  // Format price history for chart display
  const getPriceHistoryData = () => {
    if (!financialData?.priceHistory) return [];
    
    // Format date for display and get the last 30 days of data
    return financialData.priceHistory
      .slice(0, 30)
      .map(price => ({
        date: new Date(price.date).toLocaleDateString(),
        close: price.close,
        volume: price.volume / 1000000, // Convert to millions for better display
      }))
      .reverse(); // Display oldest to newest
  };

  // Get overall triple point signal
  const getTriplePointSignal = () => {
    if (!capeScore || !fScore || !pnfChart) return null;
    
    const capeSignal = (capeScore.capePercentile || 0) <= 30 ? 'Buy' : 
                      (capeScore.capePercentile || 0) >= 70 ? 'Sell' : 'Hold';
                      
    const fScoreSignal = fScore.score >= 7 ? 'Buy' : 
                        fScore.score <= 3 ? 'Sell' : 'Hold';
                        
    const pnfSignal = pnfChart.patterns?.tripleTopBreakout || pnfChart.patterns?.quadTopBreakout ? 'Buy' :
                     pnfChart.patterns?.tripleTopBreakdown || pnfChart.patterns?.quadTopBreakdown ? 'Sell' : 'Hold';
    
    // If 2 or more signals match, use that signal
    if ((capeSignal === 'Buy' && fScoreSignal === 'Buy') || 
        (capeSignal === 'Buy' && pnfSignal === 'Buy') || 
        (fScoreSignal === 'Buy' && pnfSignal === 'Buy')) {
      return 'Buy';
    } else if ((capeSignal === 'Sell' && fScoreSignal === 'Sell') || 
              (capeSignal === 'Sell' && pnfSignal === 'Sell') || 
              (fScoreSignal === 'Sell' && pnfSignal === 'Sell')) {
      return 'Sell';
    } else {
      return 'Hold';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-primary-600">Loading analysis...</p>
      </div>
    );
  }

  const signal = getTriplePointSignal();
  const signalClass = signal === 'Buy' ? 'text-green-600 dark:text-green-400' :
                    signal === 'Sell' ? 'text-red-600 dark:text-red-400' :
                    'text-yellow-600 dark:text-yellow-400';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="page-title">Stock Analysis</h1>
        {signal && (
          <div className={`px-4 py-2 rounded-lg font-bold text-lg ${signalClass}`}>
            Triple Point Signal: {signal}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border-l-4 border-red-400 dark:border-red-600 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 dark:text-red-300" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="mb-6">
          <label htmlFor="ticker-select" className="label">Select Stock</label>
          <select
            id="ticker-select"
            value={selectedTicker}
            onChange={(e) => setSelectedTicker(e.target.value)}
            className="input block w-full lg:w-64"
          >
            {tickers.map((item) => (
              <option key={item.ticker} value={item.ticker}>
                {item.ticker} - {item.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tab navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <ul className="flex flex-wrap -mb-px">
            <li className="mr-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`inline-block p-4 rounded-t-lg ${
                  activeTab === 'overview'
                    ? 'text-primary-600 border-b-2 border-primary-600 active dark:text-primary-500 dark:border-primary-500'
                    : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                }`}
              >
                Overview
              </button>
            </li>
            <li className="mr-2">
              <button
                onClick={() => setActiveTab('financial')}
                className={`inline-block p-4 rounded-t-lg ${
                  activeTab === 'financial'
                    ? 'text-primary-600 border-b-2 border-primary-600 active dark:text-primary-500 dark:border-primary-500'
                    : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                }`}
              >
                Financial Metrics
              </button>
            </li>
            <li className="mr-2">
              <button
                onClick={() => setActiveTab('technical')}
                className={`inline-block p-4 rounded-t-lg ${
                  activeTab === 'technical'
                    ? 'text-primary-600 border-b-2 border-primary-600 active dark:text-primary-500 dark:border-primary-500'
                    : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                }`}
              >
                Technical Analysis
              </button>
            </li>
          </ul>
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && capeScore && fScore && (
          <>
            {/* Stock Info & Summary */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 mb-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="section-title mb-2">CAPE Ratio</h3>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{capeScore.cape ? capeScore.cape.toFixed(2) : 'N/A'}</div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Percentile: {capeScore.capePercentile ? (capeScore.capePercentile * 100).toFixed(1) : 'N/A'}%</p>
                <div className={`mt-2 text-sm font-medium ${
                  (capeScore.capePercentile || 0) <= 0.2 ? 'text-green-600 dark:text-green-400' : 
                  (capeScore.capePercentile || 0) >= 0.8 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {(capeScore.capePercentile || 0) <= 0.2 ? 'Potentially Undervalued' : 
                  (capeScore.capePercentile || 0) >= 0.8 ? 'Potentially Overvalued' : 'Neutral Valuation'}
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="section-title mb-2">F-Score</h3>
                <div className="text-3xl font-bold text-gray-900 dark:text-white">{fScore.score} / 9</div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mt-2">
                  <div 
                    className={`h-2.5 rounded-full ${
                      fScore.score >= 7 ? 'bg-green-600' : 
                      fScore.score <= 3 ? 'bg-red-600' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${(fScore.score / 9) * 100}%` }}
                  ></div>
                </div>
                <div className={`mt-2 text-sm font-medium ${
                  fScore.score >= 7 ? 'text-green-600 dark:text-green-400' : 
                  fScore.score <= 3 ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
                }`}>
                  {fScore.score >= 7 ? 'Strong Financial Health' : 
                  fScore.score <= 3 ? 'Weak Financial Health' : 'Average Financial Health'}
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="section-title mb-2">Pattern Detection</h3>
                {pnfChart && pnfChart.patterns ? (
                  <div>
                    {(pnfChart.patterns.tripleTopBreakout || pnfChart.patterns.quadTopBreakout) ? (
                      <div className="text-green-600 dark:text-green-400 font-medium">
                        {pnfChart.patterns.tripleTopBreakout ? 'Triple Top Breakout' : 'Quad Top Breakout'}
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Bullish signal indicating potential upward movement</p>
                      </div>
                    ) : (pnfChart.patterns.tripleTopBreakdown || pnfChart.patterns.quadTopBreakdown) ? (
                      <div className="text-red-600 dark:text-red-400 font-medium">
                        {pnfChart.patterns.tripleTopBreakdown ? 'Triple Top Breakdown' : 'Quad Top Breakdown'}
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Bearish signal indicating potential downward movement</p>
                      </div>
                    ) : (
                      <div className="text-gray-600 dark:text-gray-400">
                        No Pattern Detected
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">No clear technical signal at this time</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400">No chart data available</div>
                )}
              </div>
            </div>

            {/* Price Chart */}
            {financialData && financialData.priceHistory && financialData.priceHistory.length > 0 && (
              <div className="mb-6">
                <h3 className="section-title mb-4">Price History (Last 30 Days)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={getPriceHistoryData()}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="close" stroke="#8884d8" activeDot={{ r: 8 }} name="Closing Price" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'financial' && fScore && (
          <>
            {/* F-Score Breakdown */}
            <div className="mb-6">
              <h3 className="section-title mb-4">F-Score Breakdown</h3>
              <FScoreBreakdown fScore={fScore} />
            </div>

            {/* Financial Metrics */}
            {financialData && (
              <>
                <h3 className="section-title mt-8 mb-4">Income Statement</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Metric</th>
                        {financialData.incomeStatement?.slice(0, 3).map((stmt: any, i: number) => (
                          <th key={i} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {stmt.year}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">Revenue</td>
                        {financialData.incomeStatement?.slice(0, 3).map((stmt: any, i: number) => (
                          <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            ${stmt.revenue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">Gross Profit</td>
                        {financialData.incomeStatement?.slice(0, 3).map((stmt: any, i: number) => (
                          <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            ${stmt.gross_profit.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">Net Income</td>
                        {financialData.incomeStatement?.slice(0, 3).map((stmt: any, i: number) => (
                          <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            ${stmt.net_income.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">EPS</td>
                        {financialData.incomeStatement?.slice(0, 3).map((stmt: any, i: number) => (
                          <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            ${stmt.earnings_per_share.toFixed(2)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'technical' && (
          <>
            {/* P&F Chart */}
            {pnfChart && (
              <div>
                <h3 className="section-title mb-4">Point & Figure Chart</h3>
                <PnFChart chart={pnfChart} width={800} height={400} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StockAnalysis; 